import React from 'react';

import PageLoading from 'app/components/common/PageLoading';
import SignInRequiredRoute from 'app/components/routes/SignInRequiredRoute';
import loadable from 'loadable-components';

const delay = (ms) =>
  new Promise((resolve) => {
    setTimeout(() => resolve(), ms);
  });

const delayLoad = (func, ms = 200) => {
  if (typeof window === 'undefined') return func;
  return () => Promise.all([func, delay(ms)]).then((val) => val[0]());
};

const getAsyncComponent = (asyncModule, webpackModule) =>
  loadable(delayLoad(asyncModule), {
    modules: [webpackModule],
    LoadingComponent: () => <PageLoading />,
  });

const routes = [
  {
    name: 'Home',
    screenName: '홈',
    path: '/',
    exact: true,
    component: getAsyncComponent(() => import('./front/Home'), './front/Home'),
  },
  {
    name: 'Maintenance',
    screenName: '점검',
    path: '/maintenance',
    exact: true,
    component: getAsyncComponent(
      () => import('./front/Maintenance'),
      './front/Maintenance'
    ),
  },
  {
    name: 'SignIn',
    path: '/signin',
    component: getAsyncComponent(
      () => import('./front/SignIn'),
      './front/SignIn'
    ),
  },
  {
    name: 'SignIn',
    path: '/signin-web',
    component: getAsyncComponent(
      () => import('./front/SignIn/web.jsx'),
      './front/SignIn/web.jsx'
    ),
  },
  {
    name: 'SignInOauth',
    path: '/signin/:type/oauth',
    component: getAsyncComponent(
      () => import('./front/SignInOauth'),
      './front/SignInOauth'
    ),
  },
  {
    name: 'SignUp',
    path: '/signup',
    component: getAsyncComponent(
      () => import('./front/SignUp'),
      './front/SignUp'
    ),
  },
  {
    name: 'RecoveryUser',
    path: '/recovery-user',
    component: getAsyncComponent(
      () => import('./front/RecoveryUser'),
      './front/RecoveryUser'
    ),
  },
  {
    name: 'RecoveryUserSelect',
    path: '/recovery-user-select',
    component: getAsyncComponent(
      () => import('./front/RecoveryUserSelect'),
      './front/RecoveryUserSelect'
    ),
  },
  {
    name: 'CurationList',
    screenName: '큐레이션',
    path: '/curations/:sectionId(\\d+)',
    component: getAsyncComponent(
      () => import('./front/CurationList'),
      './front/CurationList'
    ),
  },
  {
    name: 'Payment',
    path: '/payment',
    screenName: '결제',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./front/Payment'),
      './front/Payment'
    ),
    exact: true,
  },
  {
    name: 'IMPPayment',
    screenName: 'imp결제',
    path: '/imp-payment',
    component: getAsyncComponent(
      () => import('./front/ImpPayment'),
      './front/ImpPayment'
    ),
    exact: true,
  },
  {
    name: 'ImpPaymentSuccess',
    path: '/imp-payment/success',
    screenName: 'imp결제 성공',
    component: getAsyncComponent(
      () => import('./front/ImpPaymentSuccess'),
      './front/ImpPaymentSuccess'
    ),
    exact: true,
  },
  {
    name: 'PaymentHistory',
    path: '/payment-histories',
    screenName: '결제 기록',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./front/PaymentHistory'),
      './front/PaymentHistory'
    ),
    exact: true,
  },
  {
    name: 'PaymentSuccess',
    path: '/payment/success',
    screenName: '결제 성공',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./front/PaymentSuccess'),
      './front/PaymentSuccess'
    ),
    exact: true,
  },
  {
    name: 'NoticeList',
    screenName: '공지사항',
    path: '/notices',
    component: getAsyncComponent(
      () => import('./front/NoticeList'),
      './front/NoticeList'
    ),
  },
  {
    name: 'NoticeDetail',
    screenName: '공지사항 상세',
    path: '/notices/:noticeId(\\d+)',
    component: getAsyncComponent(
      () => import('./front/NoticeDetail'),
      './front/NoticeDetail'
    ),
  },
  {
    name: 'Library',
    screenName: '메인/보관함',
    path: '/library',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./front/Library'),
      './front/Library'
    ),
  },
  {
    name: 'Vouchers',
    path: '/vouchers',
    screenName: '유저 상세/쿠폰함',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./front/Vouchers'),
      './front/Vouchers'
    ),
  },
  {
    name: 'EmailVerify',
    path: '/email-verify',
    screenName: '이메일 인증',
    component: getAsyncComponent(
      () => import('./front/EmailVerify'),
      './front/EmailVerify'
    ),
  },
  {
    name: 'AdultCheck',
    path: '/adult-check',
    screenName: '성인 인증',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./front/AdultCheck'),
      './front/AdultCheck'
    ),
  },
  {
    name: 'Setting',
    path: '/setting',
    screenName: '유저 상세/설정',
    component: getAsyncComponent(
      () => import('./front/Setting'),
      './front/Setting'
    ),
  },
  {
    name: 'AccountSetting',
    path: '/account-setting',
    screenName: '유저 상세/계정 설정',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./front/AccountSetting'),
      './front/AccountSetting'
    ),
  },
  {
    name: 'AccountSettingPhone',
    path: '/account-setting/phone',
    screenName: '유저 상세/휴대폰 번호 설정',
    exact: true,
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./front/AccountSettingPhone'),
      './front/AccountSettingPhone'
    ),
  },
  {
    name: 'AccountSettingSocial',
    path: '/account-setting/social',
    screenName: '유저 상세/소셜 설정',
    exact: true,
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./front/AccountSettingSocial'),
      './front/AccountSettingSocial'
    ),
  },
  {
    name: 'AccountSettingEmailOrPassword',
    path: '/account-setting/:target',
    screenName: '유저 상세/계정 설정',
    exact: true,
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./front/AccountSettingEmailOrPassword'),
      './front/AccountSettingEmailOrPassword'
    ),
  },
  {
    name: 'UserSetting',
    path: '/user-setting',
    screenName: '유저 상세/유저 정보 수정',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./front/UserSetting'),
      './front/UserSetting'
    ),
  },
  {
    name: 'UserSettingEdit',
    path: '/user-setting/:field',
    screenName: '유저 상세/유저 정보 수정/상세',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./front/UserSettingEdit'),
      './front/UserSettingEdit'
    ),
  },
  {
    name: 'StoryDetail',
    screenName: '작품 상세',
    path: '/stories/:storyId(\\d+)',
    exact: true,
    component: getAsyncComponent(
      () => import('./front/StoryDetail/route'),
      './front/StoryDetail/route'
    ),
  },
  {
    name: 'EpisodeDetail',
    screenName: '회차 상세',
    path: '/stories/:storyId(\\d+)/episodes/:episodeId(\\d+)',
    exact: true,
    component: getAsyncComponent(
      () => import('./front/EpisodeDetail'),
      './front/EpisodeDetail'
    ),
  },
  {
    name: 'StoryCommentList',
    screenName: '작품 댓글',
    path: '/stories/:storyId(\\d+)/comments',
    component: getAsyncComponent(
      () => import('./front/StoryCommentList'),
      './front/StoryCommentList'
    ),
  },
  {
    name: 'EpisodeCommentList',
    screenName: '회차 댓글',
    path: '/stories/:storyId(\\d+)/episodes/:episodeId(\\d+)/comments',
    component: getAsyncComponent(
      () => import('./front/EpisodeCommentList'),
      './front/EpisodeCommentList'
    ),
  },
  {
    name: 'ReplyCommentList',
    screenName: '답글 목록',
    path: '/stories/:storyId(\\d+)/comments/:commentId(\\d+)/replies',
    component: getAsyncComponent(
      () => import('./front/ReplyCommentList'),
      './front/ReplyCommentList'
    ),
  },
  {
    name: 'UserDetail',
    screenName: '유저 상세',
    path: '/users/:userId(\\d+)',
    component: getAsyncComponent(
      () => import('./front/UserDetail'),
      './front/UserDetail'
    ),
  },
  {
    name: 'FollowerList',
    screenName: '유저 상세/팔로워',
    path: '/users/:userId(\\d+)/followers',
    component: getAsyncComponent(
      () => import('./front/UserFollowerList'),
      './front/FollowerList'
    ),
  },
  {
    name: 'FollowingList',
    screenName: '유저 상세/팔로잉',
    path: '/users/:userId(\\d+)/followings',
    component: getAsyncComponent(
      () => import('./front/UserFollowingList'),
      './front/FollowingList'
    ),
  },
  {
    name: 'NotificationList',
    screenName: '알림함',
    path: '/notifications',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./front/NotificationList'),
      './front/NotificationList'
    ),
  },
  {
    name: 'Search',
    screenName: '메인/검색',
    path: '/search',
    component: getAsyncComponent(
      () => import('./front/Search'),
      './front/Search'
    ),
  },
  {
    name: 'SearchTagDetail',
    screenName: '태그',
    path: '/search/tags/:tagId(\\d+)',
    component: getAsyncComponent(
      () => import('./front/TagDetail'),
      './front/TagDetail'
    ),
  },
  {
    name: 'SearchResult',
    screenName: '검색 결과',
    path: '/search-result',
    component: getAsyncComponent(
      () => import('./front/SearchResult'),
      './front/SearchResult'
    ),
  },
  {
    name: 'TagDetail',
    screenName: '태그',
    path: '/tags/:tagId(\\d+)',
    component: getAsyncComponent(
      () => import('./front/TagDetail'),
      './front/TagDetail'
    ),
  },
  {
    name: 'Unregister',
    path: '/unregister',
    component: getAsyncComponent(
      () => import('./front/Unregister'),
      './front/Unregister'
    ),
  },
  {
    name: 'CheerMore',
    screenName: '응원 목록',
    path: '/stories/:storyId(\\d+)/cheers',
    component: getAsyncComponent(
      () => import('./front/CheerMore'),
      './front/CheerMore'
    ),
  },
  {
    name: 'UserList',
    screenName: '유저 목록',
    path: '/user-list/:sectionId(\\d+)',
    component: getAsyncComponent(
      () => import('./front/UserList'),
      './front/UserList'
    ),
  },
  {
    name: 'ImpCertification',
    screenName: '아임포트 인증서',
    path: '/imp-certification',
    exact: true,
    component: getAsyncComponent(
      () => import('./front/ImpCertification'),
      './front/ImpCertification'
    ),
  },
  {
    name: 'ImpCertificationWeb',
    screenName: '아임포트 인증서 웹',
    path: '/imp-certification-web',
    exact: true,
    component: getAsyncComponent(
      () => import('./front/ImpCertificationWeb'),
      './front/ImpCertificationWeb'
    ),
  },
  {
    name: 'Coupon',
    screenName: '쿠폰 등록',
    path: '/coupons',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./front/Coupon'),
      './front/Coupon'
    ),
  },
  {
    name: 'Report',
    screenName: '신고하기',
    path: '/report',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./front/Report'),
      './front/Report'
    ),
  },
  {
    name: 'WatchLaterList',
    screenName: '좋아요한 작품 목록',
    path: '/watch-later',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./front/WatchLaterList'),
      './front/WatchLaterList'
    ),
  },
  {
    name: 'BlockedUserList',
    screenName: '차단한 유저 목록',
    path: '/blocked-users',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./front/BlockedUserList'),
      './front/BlockedUserList'
    ),
  },
  {
    name: 'BadgeList',
    screenName: '배지 목록',
    path: '/badges',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./front/BadgeList'),
      './front/BadgeList'
    ),
  },
  {
    name: 'Market',
    screenName: '마켓',
    path: '/market',
    component: getAsyncComponent(
      () => import('./front/Market'),
      './front/Market'
    ),
  },
  {
    name: 'MarketInventoryList',
    screenName: '마켓/보관함',
    path: '/market/inventories',
    component: getAsyncComponent(
      () => import('./front/MarketInventoryList'),
      './front/MarketInventoryList'
    ),
  },
  {
    name: 'Withdrawal',
    screenName: '풍선 환전',
    path: '/withdrawals',
    component: getAsyncComponent(
      () => import('./front/Withdrawal'),
      './front/Withdrawal'
    ),
  },
  {
    name: 'CommunityPostCreate',
    screenName: '커뮤니티/생성',
    path: '/community/posts/create',
    component: getAsyncComponent(
      () => import('./front/CommunityPostCreateEdit'),
      './front/CommunityCreateEdit'
    ),
  },
  {
    name: 'CommunityPostEdit',
    screenName: '커뮤니티/수정',
    path: '/community/posts/:postId(\\d+)/edit',
    component: getAsyncComponent(
      () => import('./front/CommunityPostCreateEdit'),
      './front/CommunityCreateEdit'
    ),
  },
  {
    name: 'CommunityPostDetail',
    screenName: '커뮤니티/상세',
    path: '/community/posts/:postId(\\d+)',
    component: getAsyncComponent(
      () => import('./front/CommunityPostDetail'),
      './front/CommunityDetail'
    ),
  },
  {
    name: 'CommunityPostReplyCommentList',
    screenName: '답글',
    path: '/community/posts/:postId(\\d+)/comments/:commentId(\\d+)/replies',
    component: getAsyncComponent(
      () => import('./front/ReplyCommentList'),
      './front/ReplyCommentList'
    ),
  },
  {
    name: 'FanClub',
    screenName: '팬클럽',
    path: '/users/:userId(\\d+)/fan-club',
    component: getAsyncComponent(
      () => import('./front/FanClub'),
      './front/FanClub'
    ),
  },
  {
    name: 'FanClub',
    screenName: '팬클럽',
    path: '/me/fan-club',
    component: getAsyncComponent(
      () => import('./front/FanClub'),
      './front/FanClub'
    ),
  },
  {
    name: 'FanClubEdit',
    screenName: '팬클럽/수정',
    path: '/me/fan-club/edit',
    component: getAsyncComponent(
      () => import('./front/FanClubEdit'),
      './front/FanClubEdit'
    ),
    auth: SignInRequiredRoute,
  },
  {
    name: 'Following',
    screenName: '팔로잉 목록',
    path: '/following',
    component: getAsyncComponent(
      () => import('./front/Following'),
      './front/Following'
    ),
    auth: SignInRequiredRoute,
  },
  {
    name: 'RestrictionDetail',
    screenName: '정책 위반',
    path: '/restrictions',
    component: getAsyncComponent(
      () => import('./front/RestrictionDetail'),
      './front/RestrictionDetail'
    ),
  },
  // to
  {
    name: 'To',
    path: '/to',
    component: getAsyncComponent(() => import('./front/To'), './front/To'),
  },
  // studio
  {
    name: 'StudioMypage',
    screenName: '스튜디오',
    path: '/studio',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./studio/MyPage'),
      './studio/Mypage'
    ),
  },
  //play
  {
    name: 'Play',
    screenName: '메인/플레이',
    path: '/bloom-play',
    component: getAsyncComponent(() => import('./play'), './play'),
  },
  {
    name: 'StudioStoryDetail',
    screenName: '스튜디오/작품 상세',
    path: '/studio/stories/:storyId(\\d+)',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./studio/StoryDetail'),
      './studio/StoryDetail'
    ),
  },
  {
    name: 'StudioStoryCreate',
    screenName: '스튜디오/작품 생성',
    path: '/studio/stories/create',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./studio/StoryCreateEdit'),
      './studio/StoryCreateEdit'
    ),
  },
  {
    name: 'StudioStoryEdit',
    screenName: '스튜디오/작품 수정',
    path: '/studio/stories/:storyId(\\d+)/edit',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./studio/StoryCreateEdit'),
      './studio/StoryCreateEdit'
    ),
  },
  {
    name: 'StudioEpisodeCreate',
    screenName: '스튜디오/회차 작성',
    path: '/studio/stories/:storyId(\\d+)/episodes/:episodeId(\\d+)/create',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./studio/EpisodeCreateEdit'),
      './studio/EpisodeCreateEdit'
    ),
  },
  {
    name: 'StudioEpisodeEdit',
    screenName: '스튜디오/회차 작성',
    path: '/studio/stories/:storyId(\\d+)/episodes/:episodeId(\\d+)/edit',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./studio/EpisodeCreateEdit'),
      './studio/EpisodeCreateEdit'
    ),
  },
  {
    name: 'StudioEpisodePreview',
    screenName: '스튜디오/회차 미리보기',
    path: '/studio/stories/:storyId(\\d+)/episodes/:episodeId(\\d+)/preview',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./studio/EpisodePreview'),
      './studio/EpisodePreview'
    ),
  },
  {
    name: 'StudioStoryAd',
    screenName: '스튜디오/작품 홍보 목록',
    path: '/studio/stories/:storyId(\\d+)/ads',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./studio/StoryAd'),
      './studio/StoryAd'
    ),
  },
  {
    name: 'StudioStoryAdCreate',
    screenName: '스튜디오/작품 홍보 생성',
    path: '/studio/stories/:storyId(\\d+)/ads/create',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./studio/StoryAdCreate'),
      './studio/StoryAdCreate'
    ),
  },
  {
    name: 'StudioStoryAdInfo',
    screenName: '스튜디오/작품 홍보 설명',
    path: '/studio/story-ads/info',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./studio/StoryAd/info'),
      './studio/StoryAd/info'
    ),
  },
  {
    name: 'StudioBookingPublish',
    screenName: '스튜디오/예약 공개',
    path: '/studio/stories/:storyId(\\d+)/episodes/:episodeId(\\d+)/booking',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./studio/BookingPublish'),
      './studio/BookingPublish'
    ),
  },
  // policy
  {
    name: 'TermsOfService',
    screenName: '서비스 이용약관',
    path: '/terms-of-service',
    component: getAsyncComponent(
      () => import('./policy/TermsOfService'),
      './front/TermsOfService'
    ),
  },
  {
    name: 'TermsOfService',
    screenName: '서비스 이용약관',
    path: '/terms-of-service/:version',
    component: getAsyncComponent(
      () => import('./policy/TermsOfService/version'),
      './front/TermsOfService/version'
    ),
  },
  {
    name: 'PrivacyPolicy',
    screenName: '개인정보 처리방침',
    path: '/privacy-policy',
    component: getAsyncComponent(
      () => import('./policy/PrivacyPolicy'),
      './front/PrivacyPolicy'
    ),
  },
  {
    name: 'PrivacyPolicy',
    screenName: '개인정보 처리방침',
    path: '/privacy-policy/:version',
    component: getAsyncComponent(
      () => import('./policy/PrivacyPolicy/version'),
      './front/PrivacyPolicy/version'
    ),
  },
  {
    name: 'PrivacyCollectionAgreement',
    screenName: '개인정보 수집 및 이용 동의',
    path: '/privacy-collection-agreement',
    component: getAsyncComponent(
      () => import('./policy/PrivacyCollectionAgreement'),
      './front/PrivacyCollectionAgreement'
    ),
  },
  {
    name: 'PrivacyCollectionAgreement',
    screenName: '개인정보 수집 및 이용 동의',
    path: '/privacy-collection-agreement/:version',
    component: getAsyncComponent(
      () => import('./policy/PrivacyCollectionAgreement/version'),
      './front/PrivacyCollectionAgreement/version'
    ),
  },
  // migrations
  {
    name: 'Migration',
    path: '/migrations',
    component: getAsyncComponent(
      () => import('./front/Migration'),
      './front/Migration'
    ),
  },
  // events
  {
    name: 'EpisodeStatistics',
    screenName: '이벤트/슬기로운생활',
    path: '/events/statistic',
    component: getAsyncComponent(
      () => import('./events/EpisodeStatistics'),
      './events/EpisodeStatistics'
    ),
  },
  {
    name: 'Bloom',
    screenName: '이벤트/꾸금공모전',
    path: '/events/contests/bloom',
    component: getAsyncComponent(
      () => import('./events/contests/Bloom'),
      './events/contests/Bloom'
    ),
  },
  {
    name: 'Manhwakyung',
    screenName: '이벤트/내가쓰는만화경',
    path: '/events/manhwakyung',
    component: getAsyncComponent(
      () => import('./events/contests/Manhwakyung'),
      './events/contests/Manhwakyung'
    ),
  },
  {
    name: 'ChatRoom',
    screenName: '채팅방',
    path: '/events/situations/:id/chat-room',
    component: getAsyncComponent(
      () => import('./front/Chats/ChatRoom.jsx'),
      './front/Chats/ChatRoom'
    ),
  },
  {
    name: 'AdultChatRoom',
    screenName: '성인채팅방',
    path: '/bloom-play/situations/:id/adult-chat-room',
    component: getAsyncComponent(
      () => import('./front/Chats/AdultChatRoom.jsx'),
      './front/Chats/AdultChatRoom'
    ),
  },
  {
    name: 'CoupleChemistry',
    screenName: '커플 케미스트리',
    path: '/events/viral/couple-chemistry/:uuid',
    component: getAsyncComponent(
      () => import('./play/viral/CoupleChemistry.jsx'),
      './play/viral/CoupleChemistry'
    ),
  },
  {
    name: 'AdultChatRoom',
    screenName: 'mbti 5인방의 참견',
    path: '/events/viral/5person-meddling/:uuid',
    component: getAsyncComponent(
      () => import('./play/viral/5PersonMeddling.jsx'),
      './play/viral/5PersonMeddling'
    ),
  },
  // {
  //   name: 'BookContest',
  //   screenName: '이벤트/종이책공모전/메인',
  //   path: '/contests/book',
  //   component: getAsyncComponent(
  //     () => import('./events/contests/Book'),
  //     './events/contests/Book'
  //   ),
  // },
  // {
  //   name: 'BookContestRanking',
  //   screenName: '이벤트/종이책공모전/랭킹',
  //   path: '/contests/book/ranking',
  //   component: getAsyncComponent(
  //     () => import('./events/contests/BookRanking'),
  //     './events/contests/BookRanking'
  //   ),
  // },
  // {
  //   name: 'Contest',
  //   path: '/contest/whynot',
  //   component: getAsyncComponent(
  //     () => import('./events/contests/Whynot'),
  //     './events/contests/Whynot'
  //   ),
  // },
  // {
  //   name: 'Contest',
  //   path: '/contest/whynot/award',
  //   component: getAsyncComponent(
  //     () => import('./events/contests/WhynotAward'),
  //     './events/contests/WhynotAward'
  //   ),
  // },
  // {
  //   name: 'ContestRanking',
  //   path: '/contest/whynot/ranking',
  //   component: getAsyncComponent(
  //     () => import('./events/contests/WhynotRanking'),
  //     './events/contests/WhynotRanking'
  //   ),
  // },
  // {
  //   name: 'ContestWeb',
  //   path: '/contest/whynot/web',
  //   component: getAsyncComponent(
  //     () => import('./events/contests/WhynotWeb'),
  //     './events/contests/WhynotWeb'
  //   ),
  // },
  {
    name: 'AnnualReportYear2021',
    path: '/events/annual-reports/2021',
    screenName: '2021 챝생 보고서',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./events/annualReports/Year2021'),
      './events/annualReports/Year2021'
    ),
  },
];

export default routes;



// WEBPACK FOOTER //
// ./src/app/pages/routes.jsx